import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { 
  Lightbulb, Search, Users, Sparkles, Shield, FlaskConical, Rocket, ArrowRight,
  Thermometer, Droplets, Package, Scale, MapPin, CheckCircle, Building2, Pill,
  Home, TestTube, Hospital, Heart, Microscope, Truck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const journeyMilestones = [
  {
    year: '2025',
    title: 'Discovery',
    subtitle: 'The moment that started it all',
    icon: Lightbulb,
    color: 'from-amber-500 to-orange-500',
    content: `A routine pharmaceutical delivery revealed a hidden risk. An insulated medicine bag was opened at its destination only to find that the coolant gel packs had melted hours earlier — yet no one knew when the temperature had crossed safe limits. This moment exposed a critical gap in last-mile medicine delivery.`,
    quote: 'There was trust in the delivery. But there was no verification. No record. No way to know if the medicine was still safe.',
    insight: 'Ground reality: at distributor level across India, cold-chain medicines are routinely shipped in insulated bags with gel packs — with zero electronic monitoring. Temperature excursions go undetected and unrecorded.'
  },
  {
    year: '2025',
    title: 'Research',
    subtitle: 'A deeper realisation',
    icon: Search,
    color: 'from-blue-500 to-cyan-500',
    content: `Further observation across distribution operations showed that temperature-sensitive medicines — insulin, vaccines, and IVF hormones — were routinely transported without real-time monitoring. Despite careful handling, the last mile of healthcare logistics lacked visibility.`,
    quote: 'How can medicine delivery be verified, not just assumed?',
    tags: ['Insulin', 'Vaccines', 'IVF hormones', 'Cold-chain biologics']
  },
  {
    year: '2025',
    title: 'Team',
    subtitle: 'Building the right team',
    icon: Users,
    color: 'from-green-500 to-emerald-500',
    content: `Before building technology, a small group of engineers, logistics experts, and healthcare professionals came together with a shared belief: technology in healthcare must be built with empathy for patients first, and business second. This shared mindset laid the foundation for Track0tic.`,
    quote: '"You can build a sensor. Anyone can. What you can\'t manufacture is the instinct to care about what happens when the sensor fails."',
    insight: 'The team shared one rule from day one: no feature gets built unless someone can explain how it directly protects a patient.'
  },
  {
    year: '2025',
    title: 'Concept',
    subtitle: 'The Track0tic concept is born',
    icon: Sparkles,
    color: 'from-purple-500 to-pink-500',
    content: `The team began designing a smarter logistics system capable of reconstructing the full story of every delivery. The vision was simple yet powerful: transform medicine delivery from simple tracking to verified logistics intelligence.`,
    features: [
      { icon: Thermometer, label: 'Temperature monitoring' },
      { icon: Droplets, label: 'Humidity sensing' },
      { icon: Package, label: 'Bag-open detection' },
      { icon: Scale, label: 'Weight change tracking' },
      { icon: MapPin, label: 'GPS route verification' }
    ]
  },
  {
    year: '2026',
    title: 'Patent',
    subtitle: 'Innovation protected',
    icon: Shield,
    color: 'from-indigo-500 to-blue-600',
    content: `As the system architecture matured, the team filed a patent application to protect the core technology — a system that correlates location data, bag access events, and weight variation to verify delivery authenticity. This milestone formally established Track0tic as a novel logistics verification system.`,
    patent: {
      number: '202611029156',
      title: 'System and Method for Real-Time Environmental Monitoring and Delivery Verification in Cold-Chain Logistics and Related Methods',
      type: 'Indian Patent Application · Filed 2026'
    }
  },
  {
    year: '2026',
    title: 'Testing',
    subtitle: 'Testing in real delivery environments',
    icon: FlaskConical,
    color: 'from-teal-500 to-cyan-500',
    content: `Prototype devices were tested in realistic pharmaceutical delivery scenarios — not in a lab, but in the field. Multi-sensor telemetry was able to reconstruct the full timeline of every delivery, providing unprecedented transparency into how medicines were actually handled in transit.`,
    quote: '"For the first time, a pharmacist could see exactly what happened to a consignment between the distributor\'s cold room and his shop door."',
    tags: ['Insulin transport', 'IVF hormone delivery', 'Hospital supply routes'],
    status: 'Active phase · Hardware v2 PCB in fabrication · Mobile app QA in progress · Field trials ongoing'
  },
  {
    year: '2026',
    title: 'Launch',
    subtitle: 'Ready to launch',
    icon: Rocket,
    color: 'from-red-500 to-rose-500',
    content: `After extensive development and testing, Track0tic evolved into a complete platform — combining intelligent IoT hardware with cloud analytics, delivery verification algorithms, and compliance reporting tools. The system is now ready to serve pharmaceutical distributors, hospitals, pharmacy chains, and diagnostic laboratories.`,
    capabilities: ['Intelligent IoT devices', 'Cloud analytics', 'Delivery verification', 'Compliance reporting']
  }
];

const markets = [
  {
    icon: Building2,
    title: 'Pharmaceutical Distribution',
    type: 'Core Market',
    color: 'from-blue-500 to-blue-600',
    description: 'Distributors managing thousands of daily deliveries of insulin, vaccines, biologics, and fertility drugs rely on Track0tic for transit monitoring, bag-open verification, and cold-chain compliance reports.',
    features: ['Temperature & humidity monitoring', 'Bag opening & parcel removal events', 'Delivery compliance reports', 'Last-mile cold-chain integrity']
  },
  {
    icon: Home,
    title: 'Pharmacy Chains & Home Delivery',
    type: 'Core Market',
    color: 'from-green-500 to-green-600',
    description: 'Modern pharmacy networks offering home delivery for sensitive medicines use Track0tic to ensure temperature compliance, verify delivery conditions, and strengthen patient trust.',
    features: ['Insulin & biologic temperature assurance', 'Patient-facing delivery verification', 'Digital compliance certificates', 'Brand trust in healthcare delivery']
  },
  {
    icon: TestTube,
    title: 'Diagnostic Laboratories',
    type: 'Growing Market',
    color: 'from-purple-500 to-purple-600',
    description: 'Diagnostic labs transporting thousands of biological samples between collection centres and central laboratories depend on Track0tic to maintain sample integrity.',
    features: ['Temperature & humidity during transport', 'Unauthorized sample box detection', 'Sample integrity for accurate results', 'Lab quality standard compliance']
  },
  {
    icon: Hospital,
    title: 'Hospitals & Critical Care Supply',
    type: 'Core Market',
    color: 'from-red-500 to-red-600',
    description: 'Hospitals dependent on timely delivery of sensitive medicines and medical supplies use Track0tic to verify temperature compliance and prevent diversion.',
    features: ['Temperature-sensitive medicine delivery', 'Critical medical supply monitoring', 'Tamper & diversion prevention', 'Compliance documentation for audits']
  },
  {
    icon: Heart,
    title: 'Blood Banks & Organ Transport',
    type: 'Core Market',
    color: 'from-pink-500 to-pink-600',
    description: 'Blood and organ transportation demands strict environmental control and chain-of-custody monitoring. Track0tic provides real-time tracking for life-critical transport.',
    features: ['Real-time temperature monitoring', 'Emergency route tracking', 'Tamper detection during transit', 'Verifiable transport documentation']
  },
  {
    icon: Microscope,
    title: 'Specialty Biologics & Research',
    type: 'Emerging',
    color: 'from-indigo-500 to-indigo-600',
    description: 'Research institutions and biotech companies transporting sensitive biological materials rely on controlled environments throughout transit.',
    features: ['Environmental stability during transit', 'Secure transport monitoring', 'Research logistics protocol compliance']
  }
];

const futureMarkets = ['Food cold-chain logistics', 'High-value electronics transport', 'Specialty chemicals distribution', 'Clinical trial supply chains'];

export default function JourneyPage() {
  return (
    <div data-testid="journey-page" className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(59,130,246,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(239,68,68,0.2),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="mb-6 bg-white/10 text-white border-white/20">
              TRACK<span className="text-red-400">0</span>TIC · ORIGIN STORY
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Our <span className="italic text-blue-400">Journey</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              From a Real Delivery Problem to a Healthcare Logistics Innovation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-red-500 md:-translate-x-1/2" />
            
            {journeyMilestones.map((milestone, index) => (
              <motion.div
                key={milestone.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-start gap-8 mb-16 last:mb-0 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Content Card */}
                <div className={`flex-1 pl-20 md:pl-0 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                    {/* Year Badge */}
                    <Badge className={`mb-3 bg-gradient-to-r ${milestone.color} text-white border-0`}>
                      {milestone.year}
                    </Badge>
                    
                    <h3 className="font-heading text-2xl font-bold text-gray-900 mb-1">
                      {milestone.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">{milestone.subtitle}</p>
                    
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {milestone.content}
                    </p>
                    
                    {milestone.quote && (
                      <blockquote className={`text-sm italic text-gray-500 border-l-4 border-blue-500 pl-4 my-4 ${index % 2 === 0 ? 'md:border-r-4 md:border-l-0 md:pr-4 md:pl-0 md:text-right' : ''}`}>
                        {milestone.quote}
                      </blockquote>
                    )}
                    
                    {milestone.insight && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        {milestone.insight}
                      </div>
                    )}
                    
                    {milestone.tags && (
                      <div className={`flex flex-wrap gap-2 mt-4 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                        {milestone.tags.map((tag) => (
                          <span key={tag} className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {milestone.features && (
                      <div className={`flex flex-wrap gap-3 mt-4 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                        {milestone.features.map((feature) => (
                          <div key={feature.label} className="flex items-center gap-2 text-sm text-gray-600">
                            <feature.icon className="w-4 h-4 text-primary" />
                            <span>{feature.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {milestone.patent && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4">
                        <div className="font-mono text-sm text-indigo-600 mb-1">
                          Application No. {milestone.patent.number}
                        </div>
                        <div className="font-semibold text-gray-900 text-sm mb-1">
                          {milestone.patent.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {milestone.patent.type}
                        </div>
                      </div>
                    )}
                    
                    {milestone.status && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4 text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        {milestone.status}
                      </div>
                    )}
                    
                    {milestone.capabilities && (
                      <div className={`flex flex-wrap gap-2 mt-4 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                        {milestone.capabilities.map((cap) => (
                          <span key={cap} className="text-xs px-3 py-1.5 bg-gradient-to-r from-red-100 to-rose-100 text-red-700 rounded-full font-medium">
                            {cap}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Timeline Icon */}
                <div className="absolute left-8 md:left-1/2 w-10 h-10 md:-translate-x-1/2 -translate-y-1">
                  <div className={`w-full h-full rounded-full bg-gradient-to-br ${milestone.color} flex items-center justify-center shadow-lg`}>
                    <milestone.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                
                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets We Serve */}
      <section className="py-20 bg-white" data-testid="markets-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              MARKETS WE SERVE
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Expanding Intelligent Logistics
              <span className="block italic text-primary">Beyond Pharmaceuticals</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Track<span className="text-red-500">0</span>tic is designed as a sensor-driven logistics intelligence platform 
              capable of monitoring environmental conditions, verifying delivery events, and ensuring product integrity during transit.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {markets.map((market, index) => (
              <motion.div
                key={market.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className={`bg-gradient-to-r ${market.color} px-6 py-4`}>
                  <div className="flex items-center justify-between">
                    <market.icon className="w-8 h-8 text-white" />
                    <Badge className="bg-white/20 text-white border-white/30 text-xs">
                      {market.type}
                    </Badge>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-3">
                    {market.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {market.description}
                  </p>
                  <ul className="space-y-2">
                    {market.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Future Markets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <Badge className="bg-gray-200 text-gray-700 border-gray-300 mb-1">
                  FUTURE MARKETS
                </Badge>
                <h3 className="font-heading text-xl font-bold text-gray-900">Beyond Healthcare</h3>
              </div>
            </div>
            <p className="text-gray-600 mb-4">
              The same sensor-driven logistics intelligence platform is designed to evolve into a universal intelligent logistics monitoring solution.
            </p>
            <div className="flex flex-wrap gap-2">
              {futureMarkets.map((market) => (
                <span key={market} className="text-sm px-4 py-2 bg-white rounded-full text-gray-700 border border-gray-200">
                  {market}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Looking Ahead */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-6 bg-white/20 text-white border-white/30">
              LOOKING AHEAD
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              A New Standard in Healthcare Logistics
            </h2>
            <p className="text-blue-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              Track<span className="text-red-300">0</span>tic represents a new standard in healthcare logistics — 
              a future where every medicine delivery is transparent, verified, and accountable. 
              <span className="block mt-4 font-medium text-white">
                Because patient safety begins long before the medicine reaches the patient.
              </span>
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/features">
                <Button 
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold h-14 px-8 rounded-lg"
                >
                  Explore Features
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 h-14 px-8 rounded-lg"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
